// Helpers compartilhados do CRM.

export const soDigitos = (s) => String(s || "").replace(/\D/g, "");

// Número para discagem no NVOIP: nacional, sem o DDI 55 (o discador adiciona o que precisar).
export const paraDiscagem = (tel) => {
  const d = soDigitos(tel);
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) return d.slice(2);
  return d;
};

export const formatPhone = (tel) => {
  let d = soDigitos(tel);
  if (d.startsWith("55") && d.length >= 12) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return tel || "";
};

export const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// 'YYYY-MM-DD' puro o JS lê como UTC e, em UTC-3, volta um dia — a data de
// vencimento aparecia sempre um dia antes. Acrescentar a hora força a leitura
// como data local; string com hora já vem certa e passa direto.
export const formatDate = (d) => {
  if (!d) return "";
  const bruto = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d;
  return new Date(bruto).toLocaleDateString("pt-BR");
};

export const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : "";

export const horaMsg = (d) =>
  d ? new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";

// Documento: 14 dígitos = CNPJ (PJ), senão CPF.
export const ehCNPJ = (doc) => soDigitos(doc).length === 14;

export const formatDoc = (doc) => {
  const d = soDigitos(doc);
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  return doc || "";
};

export const iniciais = (nome) => {
  if (!nome) return "?";
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes[1]?.[0] || "")).toUpperCase() || "?";
};

export const ROTULO_REGRA = {
  cnpj: "CNPJ com alto volume de compras",
  recorrente: "Clientes recorrentes",
  recompra: "Recompra (sumiram há um tempo)",
  ticket: "Ticket médio alto",
  ml_exclusivo: "Exclusivos do Mercado Livre (CNPJ)",
};

// ── Carteira (V0) ──

// Saúde RFM → cor/rótulo do sinal de risco.
export const SAUDE = {
  verde: { label: "Saudável", cor: "green", hex: "#22c55e" },
  amarelo: { label: "Atenção", cor: "yellow", hex: "#eab308" },
  vermelho: { label: "Em risco", cor: "red", hex: "#ef4444" },
  sem_compra: { label: "Sem compra", cor: "gray", hex: "#9ca3af" },
};
export const saudeInfo = (s) => SAUDE[s] || SAUDE.sem_compra;

export const SEGMENTOS = [
  { value: "autonomo", label: "Autônomo e possuo o próprio veículo" },
  { value: "autopecas_revenda", label: "Autopeças / Revenda" },
  { value: "mecanica", label: "Mecânica" },
  { value: "transportadora", label: "Transportadora" },
  { value: "locadora", label: "Locadora" },
  { value: "frota_pequena", label: "Empresa com pequena frota própria (até 5 veículos pesados)" },
  { value: "frota_grande", label: "Empresa com frota própria (acima de 5 veículos pesados)" },
  { value: "outro", label: "Outro" },
];
export const rotuloSegmento = (v) => SEGMENTOS.find((s) => s.value === v)?.label || (v || "—");

export const RESULTADOS = [
  { value: "atendeu", label: "Atendeu" },
  { value: "nao_atendeu", label: "Não atendeu" },
  { value: "vai_comprar", label: "Vai comprar" },
  { value: "sem_interesse", label: "Sem interesse" },
];
export const rotuloResultado = (v) => RESULTADOS.find((r) => r.value === v)?.label || (v || "—");
export const corResultado = (v) =>
  ({ atendeu: "green", vai_comprar: "orange", nao_atendeu: "gray", sem_interesse: "red" }[v] || "gray");

// Recência (dias) → texto curto.
export const recenciaLabel = (dias) => {
  if (dias === null || dias === undefined) return "nunca comprou";
  if (dias <= 0) return "hoje";
  if (dias === 1) return "ontem";
  return `há ${dias} dias`;
};

// ── Tarefas (V1.7) ──

// Prioridade passou a ter 3 níveis (0 baixa, 1 média, 2 alta).
export const PRIORIDADES = [
  { value: 0, label: "Baixa", cor: "gray" },
  { value: 1, label: "Média", cor: "orange" },
  { value: 2, label: "Alta", cor: "red" },
];
export const prioridadeInfo = (v) => PRIORIDADES.find((p) => p.value === Number(v)) || PRIORIDADES[0];

// Catálogo das 7 tarefas automáticas. O back-end manda o mesmo em `catalogo`;
// isto aqui é o fallback e a fonte dos rótulos curtos do card.
export const TAREFA_TIPOS = {
  abertura: { codigo: "1.1", curto: "Abertura" },
  toque_periodico: { codigo: "2.1", curto: "Toque periódico" },
  resgate: { codigo: "4.1", curto: "Resgate" },
  reativar_alto_valor: { codigo: "4.2", curto: "Alto valor" },
  pos_venda: { codigo: "6.1", curto: "Pós-venda" },
  validar_frota: { codigo: "7.1", curto: "Validar frota" },
  definir_segmento: { codigo: "7.2", curto: "Segmento" },
};
export const tarefaTipoInfo = (tipo) => TAREFA_TIPOS[tipo] || null;

// Canal do toque: vira a atividade que o desfecho registra no CRM.
export const CANAIS_TOQUE = [
  { value: "ligacao", label: "Ligação" },
  { value: "whatsapp", label: "WhatsApp" },
];

// Desfecho em três eixos.
export const EIXO_ESFORCO = [
  { value: "tentou", label: "Tentei o contato" },
  { value: "nao_tentou", label: "Não consegui tentar" },
];
export const EIXO_CONTATO = [
  { value: "respondido", label: "O cliente respondeu" },
  { value: "nao_respondido", label: "O cliente não respondeu" },
];
export const EIXO_OBJETIVO = [
  { value: "cumprido", label: "Objetivo cumprido" },
  { value: "nao_cumprido", label: "Objetivo não cumprido" },
];

// Desdobramentos: os dois primeiros já encadeiam a próxima tarefa nesta leva;
// os demais ficam registrados no histórico até a leva seguinte.
export const DESDOBRAMENTOS = [
  { value: "VENDA", label: "Virou venda", efeito: "gera a tarefa de Pós-venda (6.1)", encadeia: true },
  { value: "PERDIDO", label: "Cliente perdido", efeito: "põe a conta em cooldown", encadeia: true },
  { value: "PROBLEMA", label: "Apareceu um problema", efeito: "registrado (4.3 entra na próxima leva)", encadeia: false },
  { value: "RETORNO_AGENDADO", label: "Retorno agendado", efeito: "registrado (5.3 entra na próxima leva)", encadeia: false },
  { value: "NOVA_NECESSIDADE", label: "Nova necessidade", efeito: "registrado (Demanda/Expansão na próxima leva)", encadeia: false },
];

// Os 4 objetivos internos da 1.1 (captura parcial).
export const OBJETIVOS_ABERTURA = [
  { value: "apresentacao", label: "1.1.1 Me apresentei como contato dedicado" },
  { value: "frota", label: "1.1.2 Mapeei a frota (marca, modelo, ano, quantidade)" },
  { value: "contato", label: "1.1.3 Peguei o contato certo (nome, cargo, WhatsApp)" },
  { value: "segmento", label: "1.1.4 Confirmei o segmento" },
];

// Percentual das métricas: null (sem base) vira travessão.
export const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(0)}%`);
